/**
 * Timezone primitives — pure, framework-agnostic helpers shared by quiet-hours
 * evaluation and scheduled-message anchoring:
 *   - read the calendar parts of an instant as observed in a given IANA zone
 *   - convert wall-clock parts (interpreted in a zone) back to a UTC instant,
 *     DST-correct
 *   - compute calendar-day keys / spans in a zone
 *   - resolve the contact's timezone from a case
 *   - measure UTC offsets for a zone, or between two zones, at an instant
 *   - report whether a zone is currently observing daylight saving time
 *
 * No quiet-hours-specific logic lives here.
 */

import type { Job } from '@app/types/api/job';
import { DEFAULT_TIMEZONE } from '@app/types/api/organization';

/** Calendar parts of an instant as read in a given timezone (not a UTC moment). */
export interface ZonedDateParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  dayOfWeek: number; // 0=Sun..6=Sat (matches Date#getDay)
}

const DAY_NAME_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

// `Intl.DateTimeFormat` construction is expensive and react-datepicker re-runs
// this for every time slot and day cell on each render. A formatter is immutable
// and can be cached by time zone (all other inputs are currently fixed)
const zonedPartsFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getZonedPartsFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = zonedPartsFormatterCache.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  zonedPartsFormatterCache.set(timeZone, formatter);
  return formatter;
};

/** Date parts (year/month/day/hour/minute/dayOfWeek) of `instant` as a
 *  calendar in `timeZone` would read them. */
export const getDateParts = (
  instant: Date,
  timeZone: string,
): ZonedDateParts => {
  const parts = getZonedPartsFormatter(timeZone).formatToParts(instant);

  const lookup: Record<string, string> = {};
  parts.forEach((p) => {
    if (p.type !== 'literal') lookup[p.type] = p.value;
  });
  // Intl can emit "24" for midnight in some locales — normalise to 0.
  const rawHour = Number(lookup.hour);
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: Number(lookup.minute),
    dayOfWeek: DAY_NAME_TO_INDEX[lookup.weekday] ?? 0,
  };
};

/**
 * How many minutes ahead of UTC `timeZone` is at `instant` — `-300` for New
 * York in winter, `-240` in summer, `+330` for Kolkata.
 *
 * NOTE: the sign is the opposite of the built-in `Date#getTimezoneOffset()`,
 * which says `+300` for that same winter New York moment. Ahead-is-positive
 * matches ISO-8601 (`-05:00`), `Intl`'s `longOffset`, and how
 * `Admin/partials/timezone-service.ts` already parses offsets. A test pins it,
 * so don't flip it to match the built-in.
 *
 * The offset is measured at `instant` rather than assumed, so DST is handled
 * automatically.
 *
 * Throws `RangeError` for an unknown zone — see `isValidTimeZone`.
 */
export const getUtcOffsetMinutes = (
  instant: Date,
  timeZone: string,
): number => {
  const parts = getDateParts(instant, timeZone);
  const partsAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  );
  // `getDateParts` only goes down to minutes, so drop seconds/ms from the
  // instant before comparing — otherwise they leak into the result.
  const instantAtMinute = Math.floor(instant.getTime() / MINUTE_MS) * MINUTE_MS;
  return (partsAsUtc - instantAtMinute) / MINUTE_MS;
};

/**
 * Convert calendar parts (interpreted in `timeZone`) to UTC ms.
 *
 * Because clocks jump twice a year, a wall-clock time can match two real
 * instants, one, or none. So this tries candidates instead of assuming one
 * offset:
 *
 *   1. Read the zone's offset 24h before and 24h after the target. The fixed
 *      24h is the crux — it guarantees we see both sides of any nearby clock
 *      change. Probing from the first candidate instead would land on the same
 *      side twice and miss the second match.
 *   2. Build an instant from each offset, keeping it only if the zone really
 *      uses that offset there.
 *   3. Two matches → the clock repeated this hour (fall back); take the earlier
 *      one. None → the clock skipped it (spring forward); move past the gap.
 *      One → the normal case.
 *
 * `Date.UTC` day/month overflow is preserved (January 32nd → February 1st);
 * `schedule-menu.tsx` relies on it for "9am tomorrow" at a month boundary.
 *
 * Throws `RangeError` for an unknown zone — see `isValidTimeZone`.
 */
export const partsToUtcMs = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number => {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute);

  const offsetBefore = getUtcOffsetMinutes(
    new Date(naiveUtc - DAY_MS),
    timeZone,
  );
  const offsetAfter = getUtcOffsetMinutes(
    new Date(naiveUtc + DAY_MS),
    timeZone,
  );
  const candidateOffsets =
    offsetBefore === offsetAfter ? [offsetBefore] : [offsetBefore, offsetAfter];

  const validInstants: number[] = [];
  candidateOffsets.forEach((offset) => {
    const candidate = naiveUtc - offset * MINUTE_MS;
    if (getUtcOffsetMinutes(new Date(candidate), timeZone) === offset) {
      validInstants.push(candidate);
    }
  });

  if (validInstants.length === 0) {
    // The clock skipped this time (spring forward), so move past the gap. The
    // smaller offset gives the later instant, which is what "after the gap"
    // means in either hemisphere — the larger one would move Sydney backwards.
    //
    // This assumes the two probes disagree, which is what made the time
    // nonexistent in the first place. If they ever matched, `Math.min` would
    // hand back the candidate that just failed its check above — a wrong time
    // with no warning. That needs two clock changes inside 48h, which no zone
    // in tzdata has.
    return naiveUtc - Math.min(offsetBefore, offsetAfter) * MINUTE_MS;
  }
  // Two matches means the clock repeated this hour. The first occurrence has
  // the larger (pre-change) offset, so the earlier instant is the right pick.
  return Math.min(...validInstants);
};

// Zone ids can come from outside the app (contact profiles, Verisk inspection
// data), and callers may validate in a loop, so results are cached per id.
const timeZoneValidityCache = new Map<string, boolean>();

/**
 * Whether the runtime knows this IANA zone id.
 *
 * The offset helpers throw on an unknown zone instead of falling back to a
 * default, because a fallback gives a confidently wrong time rather than a
 * loud failure. Anything rendering to screen should check this first and fall
 * back to generic copy.
 */
export const isValidTimeZone = (timeZone: string): boolean => {
  const cached = timeZoneValidityCache.get(timeZone);
  if (cached !== undefined) return cached;

  let valid: boolean;
  try {
    Intl.DateTimeFormat('en-US', { timeZone });
    valid = true;
  } catch {
    valid = false;
  }
  timeZoneValidityCache.set(timeZone, valid);
  return valid;
};

// Resolved once per session: `getZoneOffsetMinutes` can run for every rendered
// time slot, and building an `Intl.DateTimeFormat` is the hot spot here. The
// trade-off is that an OS timezone change mid-session isn't picked up, same as
// the app's other browser-zone reads on a long-lived page.
let cachedLocalTimeZone: string | undefined;

/** The viewer's own IANA zone. */
export const getLocalTimeZone = (): string => {
  if (cachedLocalTimeZone === undefined) {
    cachedLocalTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return cachedLocalTimeZone;
};

/**
 * How many minutes ahead of `referenceZone` the `targetZone` is at `instant` —
 * `+180` when it's 3 hours ahead. Both sides account for DST, so the answer can
 * change over the year when only one of the two zones changes its clocks
 * (Phoenix vs Denver).
 *
 * `referenceZone` defaults to the viewer's zone but can always be passed in, so
 * callers and tests never depend on ambient state.
 */
export const getZoneOffsetMinutes = (
  instant: Date,
  targetZone: string,
  referenceZone: string = getLocalTimeZone(),
): number =>
  getUtcOffsetMinutes(instant, targetZone) -
  getUtcOffsetMinutes(instant, referenceZone);

/**
 * The zone's winter (non-DST) offset for `year`: the smaller of its January and
 * July offsets.
 *
 * Taking the smaller one works in either hemisphere — northern zones gain an
 * hour in July, southern zones in January, and a zone without DST reports the
 * same value for both.
 */
const getStandardOffsetMinutes = (year: number, timeZone: string): number =>
  Math.min(
    getUtcOffsetMinutes(new Date(Date.UTC(year, 0, 1)), timeZone),
    getUtcOffsetMinutes(new Date(Date.UTC(year, 6, 1)), timeZone),
  );

/**
 * True when `timeZone` is on daylight saving time at `instant`.
 *
 * Three known limitations, all accepted:
 *   - A zone that *permanently* changed its offset mid-year (e.g. dropping DST
 *     for good) may be wrong for that year.
 *   - tzdata models Ireland backwards, as negative DST in winter, so this says
 *     `true` in Irish summer — wrong per tzdata, right per user expectation.
 *   - A DST period longer than six months would cover both January 1 and
 *     July 1, so `min(Jan, Jul)` would pick the DST offset as "standard" and
 *     this would always say `false`. No zone in tzdata does that.
 */
export const isDaylightSavingTime = (
  instant: Date,
  timeZone: string,
): boolean => {
  const { year } = getDateParts(instant, timeZone);
  return (
    getUtcOffsetMinutes(instant, timeZone) >
    getStandardOffsetMinutes(year, timeZone)
  );
};

/**
 * Contact IANA timezone for the case. Mirrors the server's
 * `resolveContactTimeZone`: the Verisk inspection-address zone wins when
 * present, then the end-user's own zone (`user.timeZone ?? profile.timeZone`).
 * Null means the caller should apply its own fallback (e.g. org default) — none
 * is applied here.
 */
export const getContactTimeZone = (job: Job | undefined): string | null => {
  const veriskTimeZone = job?.veriskData?.timeZoneData?.timeZoneId;
  if (veriskTimeZone) return veriskTimeZone;
  const customer = job?.customer;
  if (!customer) return null;
  return customer.timeZone ?? customer.profile?.timeZone ?? null;
};

/** Where a resolved timezone came from. `verisk`/`contact` are derived from the
 *  case's contact; `org`/`default` are fallbacks (no contact zone was known). */
export type TimeZoneSource = 'verisk' | 'contact' | 'org' | 'default';

export interface ResolvedTimeZone {
  /** Always a concrete IANA zone — safe to schedule or evaluate against. */
  timeZone: string;
  source: TimeZoneSource;
}

/**
 * Unified case-timezone resolver shared by quiet hours and scheduled messages.
 * Resolution order mirrors the server (`getCachedTimeZone`): Verisk inspection
 * zone → contact profile → caller's org fallback → DEFAULT_TIMEZONE. Always
 * returns a concrete zone; `source` lets callers distinguish a contact-derived
 * zone from a fallback (e.g. the QH banner renders generic copy instead of a
 * misleading contact-local time when the zone is a fallback).
 */
export const resolveCaseTimeZone = (
  job: Job | undefined,
  fallbackTimeZone: string | undefined,
): ResolvedTimeZone => {
  const veriskTz = job?.veriskData?.timeZoneData?.timeZoneId;
  if (veriskTz) return { timeZone: veriskTz, source: 'verisk' };

  const contactTz = getContactTimeZone(job);
  if (contactTz) return { timeZone: contactTz, source: 'contact' };

  if (fallbackTimeZone) return { timeZone: fallbackTimeZone, source: 'org' };

  return { timeZone: DEFAULT_TIMEZONE, source: 'default' };
};

/** True when the resolved zone is the contact's own (Verisk or profile) rather
 *  than a fallback — i.e. safe to present as the contact's local time. */
export const isContactZone = (resolved: ResolvedTimeZone): boolean =>
  resolved.source === 'verisk' || resolved.source === 'contact';

// SSM kill switch (FE-only) for the unified timezone resolution path. When off,
// quiet hours and scheduled messages use their legacy per-feature resolution.
export const isUnifiedTimezoneResolutionFeatureFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_UNIFIED_TIMEZONE_RESOLUTION as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

/**
 * Human-readable name for an IANA zone in `locale` (e.g. "Eastern Daylight
 * Time"), falling back to the raw id for unknown zones. Works for any IANA zone
 * — unlike the Admin `formatTimezoneLabel`, which assumes SUPPORTED_TIMEZONES.
 */
export const formatTimeZoneName = (
  timeZone: string,
  locale?: string,
): string => {
  try {
    const name = new Intl.DateTimeFormat(locale || 'en', {
      timeZone,
      timeZoneName: 'long',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return name || timeZone;
  } catch {
    return timeZone;
  }
};

/**
 * A browser-local Date whose calendar fields equal `instant`'s wall-clock in
 * `timeZone`. Fed to a browser-local widget (react-datepicker) so it displays
 * and bounds dates in the contact's zone rather than the agent's browser.
 */
export const toZonedDisplayDate = (instant: Date, timeZone: string): Date => {
  const parts = getDateParts(instant, timeZone);
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  );
};

/**
 * Converts a wall-clock time to a UTC instant (ms). When `timeZone` is set, the
 * wall-clock's calendar fields are interpreted in that zone. Otherwise they're
 * read in the browser's local zone.
 */
export const wallClockToUtcMs = (
  wallClock: Date,
  timeZone?: string | null,
): number =>
  timeZone
    ? partsToUtcMs(
        wallClock.getFullYear(),
        wallClock.getMonth() + 1,
        wallClock.getDate(),
        wallClock.getHours(),
        wallClock.getMinutes(),
        timeZone,
      )
    : wallClock.getTime();
